import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Loader2,
  Briefcase,
  Search,
  Calendar,
  User,
  Truck,
  MapPin,
  Package,
  ExternalLink,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { serviceTitanAPI } from '../lib/servicetitanApi';
import { format, formatDistanceToNow } from 'date-fns';

const JOB_STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  dispatched: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  canceled: 'bg-red-100 text-red-800',
  on_hold: 'bg-gray-100 text-gray-800',
};

export const ServiceTitanJobs = () => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [daysFilter, setDaysFilter] = useState('30');
  
  // Pagination
  const [page, setPage] = useState(0);
  const limit = 25;

  const loadJobs = async () => {
    try {
      setLoading(true);
      const params = {
        limit,
        offset: page * limit,
        days: parseInt(daysFilter),
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await serviceTitanAPI.getJobs(params);
      setJobs(response.data.jobs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      if (error.response?.status === 404) {
        // Jobs not available - integration not connected or jobs sync disabled
        toast.error('Jobs not available', {
          description: 'ServiceTitan must be connected with Jobs sync enabled.'
        });
      } else {
        toast.error('Failed to load jobs');
      }
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, daysFilter]);

  const loadJobDetail = async (jobId) => {
    try {
      setLoadingDetail(true);
      const response = await serviceTitanAPI.getJob(jobId);
      setJobDetail(response.data);
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    loadJobDetail(job.id);
  };

  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.st_job_number?.toLowerCase().includes(query) ||
      job.customer_name?.toLowerCase().includes(query) ||
      job.customer_address?.toLowerCase().includes(query) ||
      job.assigned_technician_name?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">ServiceTitan Jobs</h1>
              <p className="text-sm text-muted-foreground">
                View jobs synced from ServiceTitan (read-only)
              </p>
            </div>
          </div>
          <Badge variant="outline" className="gap-1 self-start">
            <ExternalLink className="w-3 h-3" />
            Source: ServiceTitan
          </Badge>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by job #, customer, address, or technician..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={daysFilter} onValueChange={setDaysFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Time Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Jobs Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Jobs ({total})</CardTitle>
              <div className="text-sm text-muted-foreground">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {searchQuery ? 'No jobs match your search' : 'No jobs found'}
              </div>
            ) : (
              <>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Scheduled</TableHead>
                        <TableHead>Technician</TableHead>
                        <TableHead>Materials</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredJobs.map((job) => (
                        <TableRow
                          key={job.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleJobClick(job)}
                        >
                          <TableCell className="font-medium">
                            {job.st_job_number || job.st_job_id}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{job.customer_name || 'N/A'}</p>
                              {job.customer_address && (
                                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {job.customer_address}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={JOB_STATUS_COLORS[job.job_status] || ''}
                            >
                              {job.job_status?.replace('_', ' ') || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {job.scheduled_start ? (
                              <div className="text-sm">
                                <p>{format(new Date(job.scheduled_start), 'MMM d, yyyy')}</p>
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(job.scheduled_start), 'h:mm a')}
                                </p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {job.assigned_technician_name || (
                              <span className="text-muted-foreground">Unassigned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {job.materials_count || 0}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Job Detail Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Job {jobDetail?.st_job_number || selectedJob?.st_job_number}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : jobDetail ? (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className={JOB_STATUS_COLORS[jobDetail.job_status] || ''}
                >
                  {jobDetail.job_status?.replace('_', ' ')}
                </Badge>
                {jobDetail.job_type && (
                  <Badge variant="outline">{jobDetail.job_type}</Badge>
                )}
              </div>

              {/* Customer */}
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{jobDetail.customer_name || 'N/A'}</p>
                    {jobDetail.customer_address && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {jobDetail.customer_address}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Summary */}
              {jobDetail.summary && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Summary</h4>
                  <p className="text-sm text-muted-foreground">{jobDetail.summary}</p>
                </div>
              )}

              {/* Schedule */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Scheduled
                  </h4>
                  {jobDetail.scheduled_start ? (
                    <p className="text-sm">
                      {format(new Date(jobDetail.scheduled_start), 'MMM d, yyyy h:mm a')}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Not scheduled</p>
                  )}
                </div>
                {jobDetail.completed_at && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">Completed</h4>
                    <p className="text-sm">
                      {format(new Date(jobDetail.completed_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                )}
              </div>

              {/* Assigned */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
                    <User className="w-3 h-3" /> Technician
                  </h4>
                  <p className="text-sm">
                    {jobDetail.assigned_technician_name || 'Unassigned'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1 flex items-center gap-1">
                    <Truck className="w-3 h-3" /> Truck
                  </h4>
                  <p className="text-sm">
                    {jobDetail.assigned_truck_name || 'None'}
                  </p>
                </div>
              </div>

              {/* Materials */}
              {jobDetail.materials && jobDetail.materials.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                    <Package className="w-3 h-3" /> Materials Used ({jobDetail.materials.length})
                  </h4>
                  <div className="max-h-[150px] overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Item</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {jobDetail.materials.map((mat, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm py-2">
                              {mat.name || mat.st_material_id}
                            </TableCell>
                            <TableCell className="text-sm py-2 text-right">
                              {mat.qty}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Sync Info */}
              <div className="pt-2 border-t text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Synced {formatDistanceToNow(new Date(jobDetail.synced_at))} ago
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ServiceTitanJobs;
